import React, { useState, useEffect } from 'react';
import { eventBriefsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { 
  X, Star, Clock, Users, ChefHat, MessageSquare, AlertTriangle, 
  FileText, CheckCircle, Send
} from 'lucide-react';

const EventCaptainBrief = ({ contract, onClose, onSave }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [existingBriefs, setExistingBriefs] = useState([]);
  const [formData, setFormData] = useState({
    contract_id: contract?.id || '',
    scheduled_start_time: contract?.start_time || '',
    actual_start_time: '',
    service_timing: 'on_time',
    delay_reason: '',
    staff_attendance: 5,
    staff_professionalism: 5,
    staff_appearance: 5,
    staff_teamwork: 5,
    staff_names: [],
    staff_issues: '',
    staff_highlights: '',
    food_quality: 5,
    food_presentation: 5,
    service_quality: 5,
    client_satisfaction: 5,
    client_feedback: '',
    issues_reported: false,
    issues_description: '',
    equipment_issues: '',
    supplies_used_notes: '',
    event_highlights: '',
    recommendations: '',
    notes: '',
    submitted_by: ''
  });
  const [staffNameInput, setStaffNameInput] = useState('');

  useEffect(() => {
    if (contract?.id) {
      loadExistingBriefs();
    }
  }, [contract?.id]);

  const loadExistingBriefs = async () => {
    try {
      const res = await eventBriefsAPI.getForContract(contract.id);
      setExistingBriefs(res.data || []);
    } catch (err) {
      console.error('Failed to load briefs:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addStaffName = () => {
    if (staffNameInput.trim()) {
      setFormData(prev => ({
        ...prev,
        staff_names: [...prev.staff_names, staffNameInput.trim()]
      }));
      setStaffNameInput('');
    }
  };

  const removeStaffName = (index) => {
    setFormData(prev => ({
      ...prev,
      staff_names: prev.staff_names.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.submitted_by) {
      toast({ title: 'Error', description: 'Please enter your name', variant: 'destructive' });
      return;
    }
    if (!formData.actual_start_time) {
      toast({ title: 'Error', description: 'Please enter actual start time', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await eventBriefsAPI.create(formData);
      toast({ title: 'Success', description: 'Event brief submitted successfully' });
      onSave?.();
      onClose();
    } catch (err) {
      toast({ 
        title: 'Error', 
        description: err.response?.data?.detail || 'Failed to submit brief', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const RatingInput = ({ label, value, field }) => (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-1 mt-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => handleChange(field, star)}
            className={`p-1 ${star <= value ? 'text-yellow-500' : 'text-gray-300'}`}
          >
            <Star className="h-6 w-6 fill-current" />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="bg-[#1e3a5f] text-white sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" /> Event Captain Brief
              </CardTitle>
              <p className="text-sm opacity-80 mt-1">{contract?.event_name} - {contract?.event_date}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="p-6 space-y-6">
            {/* Existing Briefs Warning */}
            {existingBriefs.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  {existingBriefs.length} brief(s) already submitted for this event.
                </p>
              </div>
            )}

            {/* Timing Section */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-[#1e3a5f]">
                <Clock className="h-5 w-5" /> Event Timing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Scheduled Start Time</Label>
                  <Input
                    type="time"
                    value={formData.scheduled_start_time}
                    onChange={(e) => handleChange('scheduled_start_time', e.target.value)}
                    data-testid="scheduled-time-input"
                  />
                </div>
                <div>
                  <Label>Actual Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.actual_start_time}
                    onChange={(e) => handleChange('actual_start_time', e.target.value)}
                    required
                    data-testid="actual-time-input"
                  />
                </div>
                <div>
                  <Label>Service Timing</Label>
                  <Select value={formData.service_timing} onValueChange={(v) => handleChange('service_timing', v)}>
                    <SelectTrigger data-testid="timing-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on_time">On Time</SelectItem>
                      <SelectItem value="early">Early</SelectItem>
                      <SelectItem value="late">Late</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {formData.service_timing === 'late' && (
                <div className="mt-4">
                  <Label>Delay Reason</Label>
                  <Textarea
                    value={formData.delay_reason}
                    onChange={(e) => handleChange('delay_reason', e.target.value)}
                    placeholder="Explain the delay..."
                    data-testid="delay-reason-input"
                  />
                </div>
              )}
            </div>

            {/* Staff Section */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-[#1e3a5f]">
                <Users className="h-5 w-5" /> Staff Evaluation
              </h3>
              
              {/* Staff Names */}
              <div className="mb-4">
                <Label>Staff on Duty</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={staffNameInput}
                    onChange={(e) => setStaffNameInput(e.target.value)}
                    placeholder="Enter staff name"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStaffName())}
                    data-testid="staff-name-input"
                  />
                  <Button type="button" onClick={addStaffName} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.staff_names.map((name, idx) => (
                    <span key={idx} className="px-2 py-1 bg-[#1e3a5f] text-white rounded-full text-sm flex items-center gap-1">
                      {name}
                      <button type="button" onClick={() => removeStaffName(idx)} className="hover:bg-white/20 rounded-full p-0.5">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Staff Ratings */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <RatingInput label="Attendance" value={formData.staff_attendance} field="staff_attendance" />
                <RatingInput label="Professionalism" value={formData.staff_professionalism} field="staff_professionalism" />
                <RatingInput label="Appearance" value={formData.staff_appearance} field="staff_appearance" />
                <RatingInput label="Teamwork" value={formData.staff_teamwork} field="staff_teamwork" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Staff Highlights</Label>
                  <Textarea
                    value={formData.staff_highlights}
                    onChange={(e) => handleChange('staff_highlights', e.target.value)}
                    placeholder="Any standout performances..."
                    data-testid="staff-highlights-input"
                  />
                </div>
                <div>
                  <Label>Staff Issues</Label>
                  <Textarea
                    value={formData.staff_issues}
                    onChange={(e) => handleChange('staff_issues', e.target.value)}
                    placeholder="Any issues or concerns..."
                    data-testid="staff-issues-input"
                  />
                </div>
              </div>
            </div>

            {/* Service Quality Section */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-[#1e3a5f]">
                <ChefHat className="h-5 w-5" /> Service Quality
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <RatingInput label="Food Quality" value={formData.food_quality} field="food_quality" />
                <RatingInput label="Food Presentation" value={formData.food_presentation} field="food_presentation" />
                <RatingInput label="Service Quality" value={formData.service_quality} field="service_quality" />
                <RatingInput label="Client Satisfaction" value={formData.client_satisfaction} field="client_satisfaction" />
              </div>
            </div>

            {/* Client Feedback Section */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-[#1e3a5f]">
                <MessageSquare className="h-5 w-5" /> Client Feedback
              </h3>
              <Textarea
                value={formData.client_feedback}
                onChange={(e) => handleChange('client_feedback', e.target.value)}
                placeholder="Any feedback from the client..."
                rows={3}
                data-testid="client-feedback-input"
              />
            </div>

            {/* Issues Section */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-[#1e3a5f]">
                <AlertTriangle className="h-5 w-5" /> Issues & Incidents
              </h3>
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="issues_reported"
                  checked={formData.issues_reported}
                  onChange={(e) => handleChange('issues_reported', e.target.checked)}
                  className="h-4 w-4"
                  data-testid="issues-checkbox"
                />
                <Label htmlFor="issues_reported">Issues were reported during this event</Label>
              </div>
              {formData.issues_reported && (
                <Textarea
                  value={formData.issues_description}
                  onChange={(e) => handleChange('issues_description', e.target.value)}
                  placeholder="Describe the issues..."
                  rows={3}
                  data-testid="issues-description-input"
                />
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>Equipment Issues</Label>
                  <Textarea
                    value={formData.equipment_issues}
                    onChange={(e) => handleChange('equipment_issues', e.target.value)}
                    placeholder="Any equipment problems..."
                    data-testid="equipment-issues-input"
                  />
                </div>
                <div>
                  <Label>Supplies Used Notes</Label>
                  <Textarea
                    value={formData.supplies_used_notes}
                    onChange={(e) => handleChange('supplies_used_notes', e.target.value)}
                    placeholder="Notes on supplies consumed..."
                    data-testid="supplies-notes-input"
                  />
                </div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-[#1e3a5f]">
                <CheckCircle className="h-5 w-5" /> Summary & Recommendations
              </h3>
              <div className="space-y-4">
                <div>
                  <Label>Event Highlights</Label>
                  <Textarea
                    value={formData.event_highlights}
                    onChange={(e) => handleChange('event_highlights', e.target.value)}
                    placeholder="What went well..."
                    data-testid="event-highlights-input"
                  />
                </div>
                <div>
                  <Label>Recommendations</Label>
                  <Textarea
                    value={formData.recommendations}
                    onChange={(e) => handleChange('recommendations', e.target.value)}
                    placeholder="Suggestions for improvement..."
                    data-testid="recommendations-input"
                  />
                </div>
                <div>
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Any other notes..."
                    data-testid="notes-input"
                  />
                </div>
              </div>
            </div>

            {/* Submitter */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label>Submitted By *</Label>
              <Input
                value={formData.submitted_by}
                onChange={(e) => handleChange('submitted_by', e.target.value)}
                placeholder="Your name"
                required
                data-testid="submitted-by-input"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"
                data-testid="submit-brief-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" /> Submit Brief
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
};

export default EventCaptainBrief;
